import * as a11y from '../vendor/theme-scripts/theme-a11y';
import FetchError from '../util/fetch-error';

const selectors = {
  pickupContainer: 'data-store-availability-container',
  shopifySection: '.shopify-section',
  drawer: '[data-pickup-drawer]',
  drawerOpen: '[data-pickup-drawer-open]',
  drawerClose: '[data-pickup-drawer-close]',
  focusable: 'button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
  section: '[data-section-type]',
};

const classes = {
  isHidden: 'hidden',
};

const attributes = {
  closing: 'closing',
};

class PickupAvailability extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.container = this.closest(selectors.section);
    this.drawer = null;
    this.buttonDrawerOpen = null;
    this.buttonDrawerClose = null;
    this.a11y = a11y;
    this.isAnimating = false;
    this.container.addEventListener('theme:variant:change', (event) => this.fetchPickupAvailability(event));
    this.fetchPickupAvailability();
  }

  fetchPickupAvailability(event) {
    if ((event && !event.detail.variant) || (event && event.detail.variant && !event.detail.variant.available)) {
      this.classList.add(classes.isHidden);
      return;
    }

    const variantID = event && event.detail.variant ? event.detail.variant.id : this.getAttribute(selectors.pickupContainer);

    if (variantID) {
      fetch(`${window.theme.routes.root}variants/${variantID}/?section_id=api-pickup-availability`)
        .then(this.handleErrors)
        .then((response) => response.text())
        .then((text) => {
          const pickupAvailabilityHTML = new DOMParser().parseFromString(text, 'text/html').querySelector(selectors.shopifySection).innerHTML;
          this.innerHTML = pickupAvailabilityHTML;

          this.drawer = this.querySelector(selectors.drawer);
          if (!this.drawer) {
            this.classList.add(classes.isHidden);
            return;
          }

          this.classList.remove(classes.isHidden);

          this.drawerEvents();
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  openDrawer() {
    if (this.drawer) {
      // Check if browser supports Dialog tags
      if (typeof this.drawer.showModal === 'function') {
        this.drawer.showModal();
      }
      this.drawer.setAttribute('open', true);
      this.drawer.removeAttribute('inert');
      this.drawer.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));

      const focusTarget = this.drawer.querySelector('[autofocus]') || this.drawer.querySelector(selectors.focusable);
      focusTarget.focus();

      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
    }
  }

  closeDrawer() {
    if (this.isAnimating) {
      return;
    }

    if (!this.drawer.hasAttribute(attributes.closing)) {
      this.drawer.setAttribute(attributes.closing, '');
      this.isAnimating = true;
      return;
    }

    // Check if browser supports Dialog tags
    if (typeof this.drawer.close === 'function') {
      this.drawer.close();
    } else {
      this.drawer.removeAttribute('open');
    }

    this.drawer.removeAttribute(attributes.closing);
    this.drawer.setAttribute('inert', '');

    const hasOpenModal = document.querySelector('dialog[open]');

    // Unlock scroll if no other drawers & modals are open
    if (!hasOpenModal) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }
  }

  drawerEvents() {
    this.querySelector(selectors.drawerOpen)?.addEventListener('click', () => {
      this.openDrawer();
      window.accessibility.lastElement = this.buttonDrawerOpen;
    });

    // Close button click event
    this.drawer.querySelector(selectors.drawerClose)?.addEventListener('click', (e) => {
      e.preventDefault();
      this.closeDrawer();
    });

    // Close dialog on click outside content
    this.drawer.addEventListener('click', (event) => {
      if (event.target.nodeName === 'DIALOG' && event.type === 'click') {
        this.closeDrawer();
      }
    });

    // Close dialog on click ESC key pressed
    this.drawer.addEventListener('keydown', (event) => {
      if (event.code === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
      }
    });

    // Close dialog after animation completes
    this.drawer.addEventListener('animationend', (event) => {
      if (event.target !== this.drawer) return;
      this.isAnimating = false;

      if (this.drawer.hasAttribute(attributes.closing)) {
        this.closeDrawer();
      }
    });
  }

  handleErrors(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }
}

export {PickupAvailability};
